import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CompetitionService } from '@b2b/core/api/competition/competition.service';
import { MetaDefinition } from '@angular/platform-browser';
import { ImageOptimizerService } from '@sparro/app/core/helpers/image-optimizer';
import { MetaService } from '@sparro/app/core/helpers/meta/meta.service';
import { Badge, Slide } from '@sparro/app/core/models/carousel.model';

import { environment } from '@b2b/env/environment';
import _ from 'lodash';
import { UNI_CONSTANTS } from '@sparro/app/core/config/constants.config';
import { Organization } from '@sparro/app/core/models/organization.model';

export class SEOContent {
  title: string;
  content: string;
  host: string;
}

@Component({
  selector: 'app-home-view',
  templateUrl: './home-view.component.html',
  styleUrls: ['./home-view.component.scss']
})

export class HomeViewComponent implements OnInit {
  competitionSections = [];
  competitions = [];
  adsArray: any[] = [];
  carousel: Array<Slide> = [];
  // display = 'breakpoint';
  display: ('grid' | 'breakpoint') = 'grid';
  isLoading: boolean = true;
  showSpinner: boolean = false;
  allSectionsLoaded: boolean = false;
  sectionPage: number = 1;
  next: string;
  page: number = 1;
  limit: number;
  host: string = UNI_CONSTANTS.HOSTS.ARENA;
  seoContent: SEOContent;

  constructor(
    private competitionService: CompetitionService,
    private changeDetectorRef: ChangeDetectorRef,
    public metaService: MetaService,
    public router: Router,
    public route: ActivatedRoute,
    public imageOptimizer: ImageOptimizerService,
  ) {}

  ngOnInit(): void {
    this.getFeaturedCompetitions();
    this.getBreakpointLimit();
    this.getAds();
    this.getCompetitionSections();
    this.getSeoContent();
  }

  getSeoContent() {
    this.competitionService.getSeoContent().subscribe(
      (response: SEOContent) => {
        this.seoContent = response;
      }
    );
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    const currentPos = (document.documentElement.scrollTop || document.body.scrollTop);
    const maxPos = document.documentElement.scrollHeight;
    if (currentPos >= maxPos / 2 && (this.next || !this.allSectionsLoaded) && !this.showSpinner) {
      this.loadCompetitions();
    }
  }

  getAds() {
    this.competitionService.getAd('Competition').subscribe((response: any) => {
      if (response) {
        this.adsArray = _.shuffle(response);
      }
    }, error => {
      console.log(error);
    })
  }

  @HostListener('window:resize', ['$event'])
  getBreakpointLimit(): void {
    let width = document.documentElement.offsetWidth;
    width -= width>1280 ? 340 : (width>960 ? 80 : 0);
    this.limit = Math.floor(width / 350);
  }

  getFeaturedCompetitions(): void {
    this.competitionService.getFeaturedCompetitions().subscribe(
      (response: any) => {
        const featuredArray = _.uniqBy(response.results, 'id');
        this.setCarousel(featuredArray);
        this.updateMetaInfo();
      });
  }

  updateMetaInfo(): void {
    let metaInfo: MetaDefinition[] = [];
    let title: string,
      image: string,
      description: string;

    title = `Architecture Competitions | Conceptual Architecture Design Competition- UNI`;
    description = `World's largest pool of Architecture competition.
      Join UNI and showcase your skill to the world in the field of Architecture,
      Interior, Urban Design, Landscape, and more.
      Join UNI Membership and participate in any competition on UNI for free!`;
    image = this.carousel[0].image ? this.renderImage(this.carousel[0].image) : '';

    metaInfo = [
      { name: 'title', content: title },
      { name: 'description', content: description },
      { name: 'url', content: window.location.href },
      { name: 'image', content: image },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description.slice(0, 100) },
      { name: 'twitter:url', content: window.location.href },
      { name: 'twitter:image', content: image },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: window.location.href },
      { property: 'og:image', content: image },
      { property: 'og:image:alt', content: title },
      { property: 'og:type', content: 'website' }
    ];
    metaInfo.forEach(meta => {
      this.metaService.updateMetaInfo(meta);
    });
    this.metaService.updateMeta(metaInfo);
    this.metaService.updateTitle(title);
    this.metaService.createCanonicalURL();
  }

  setCarousel(featuredCompetition): void {
    featuredCompetition.forEach(
      (competition) => {
        // console.log(competition);
        const carousel_slide_badge: Badge = new Badge(
          competition.organizer.logo,
          this.getOrganizerUrl(competition.organizer),
          Badge.TYPE.ORGANIZATION
        ).setTooltip(
          competition.hosted ? `Hosted by ${competition.organizer.title}` : `Listed by ${competition.organizer.title}`
        );
        let carousel_slide: Slide = new Slide(
          competition.image,
          competition.redirect_url ? competition.redirect_url : `${environment.competitionUrl}${competition.slug}`,
          Slide.TYPE.COMPETITION,
          true
        ).setTitle(
          competition.title,
          `Location: ${competition.location?.country ? competition.location.country.name : 'Worldwide'}`
        ).addBadge(
          carousel_slide_badge
        );
        this.carousel.push(carousel_slide);
      }
    );
  }

  getOrganizerUrl(organizer: Organization) {
    return `${environment.organizationUrl}${organizer.slug}`;
  }

  getCompetitionSections(): void {
    if (!this.showSpinner) {
      this.isLoading = true;
    }
    // if (this.showSpinner) {
    //   return;
    // }
    // if (!this.showSpinner) {
    //   this.showSpinner = true
    // }
    this.competitionService.getCompetitionSections(this.sectionPage).subscribe(
      (response: any) => {
        // console.log(response)
        let comp_list = response.results.filter((section) => {
          if (section) {
            section.visible_competitions = [];
            section.display = 'breakpoint';
            section.limit = this.limit;
            section.visible_competitions = section.competitions.slice(0, this.limit);
          }
          return section.competitions.length > 0;
        });

        this.competitionSections.push(...comp_list);
        const section_next = response.next;
        if (section_next) {
          this.sectionPage = parseInt(section_next.split('=')[1]);
        } else {
          this.allSectionsLoaded = true;
          this.getCompetitions();
        }
        this.isLoading = false;
        this.showSpinner = false;
      }, (err) => {
        console.log(err);
        this.isLoading = false;
        this.showSpinner = false;
      });
  }

  getCompetitions(): void {
    this.showSpinner = true;
    this.competitionService.getActiveCompetitions(this.page).subscribe(
      (response: any) => {
        // console.log(response);
        this.next = response.next;
        if (this.next) {
          this.page = parseInt(this.next.split('=')[2]);
        }
        this.competitions.push(response.results);
        this.showSpinner = false;
      }, (err) => {
        console.log(err);
        this.showSpinner = false;
      });
  }

  loadMore(section_obj) {
    this.competitionSections = this.competitionSections.filter((section, index) => {
      if (section.slug == section_obj.slug) {
        if (section.display == 'breakpoint') {
          section.display = 'grid';
        }
        section.limit += this.limit;
        section.visible_competitions = section.competitions.slice(0, section.limit);
      }
      return section;
    });
    this.changeDetectorRef.detectChanges();
  }

  loadCompetitions() {
    this.showSpinner = true;
    if (this.display === 'breakpoint') {
      this.display = 'grid';
      this.showSpinner = false;
      return;
    }
    if (!this.allSectionsLoaded) {
      this.getCompetitionSections();
    } else if (this.next != null) {
      this.getCompetitions();
    }
  }

  goToPreviousComp(): void {
    this.router.navigate(['search'], {
      queryParams: {
        query: '',
        timeline: 'previous',
        order: 'title',
        show: 'all'
      },
    });
  }
  goToSearchPage(searchText: (string | void)): void {
    if (searchText) {
      this.router.navigate(['search'], {
        queryParams: {
          query: searchText,
        },
      });
    }
    else {
      this.router.navigate(['search']);
    }
  }

  getSectionUrl(slug: string) {
    return `${environment.competitionUrl}section/${slug}/`;
  }


  renderImage(image: string): string {
    return this.imageOptimizer.renderImage(image, window.innerWidth, '');
  }
}
